import { LEVEL_CONFIG } from '../../../config/level.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import {
  COLLECTIBLE_FLOAT_HEIGHT,
  collectibleLine,
  entryConnector,
  jumpArc,
  laneArea,
  moduleSides,
  obstacleRow,
  standardLanes,
  straightExit,
} from '../segmentBuilders';
import { SegmentKind, type CollectibleSpawnPoint, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_04 – small construction pit 20 m (design/01_segments/SEG_04_Pit_Small).
 *
 *   z 0–5    approach (no obstacles)
 *   z 5–10   pit, exactly one lane wide (2.2 m) – or two lanes; 5 m = single-jump limit at start speed
 *   z 15     obstacle row (jump / lane change), 5 m behind the pit
 *
 * Jump over it or change lanes – the free lanes are really free. Bottles arc over
 * the pit along the jump line (risky), the other lines run straight.
 * Variants are separate modules (their greybox differs): L, C, R, LC, CR.
 */
export const PIT_SMALL = {
  length: WORLD_CONFIG.baseSegmentLength,
  zFrom: 5,
  zTo: 10,
  /** Visual depth of the pit floor. */
  depth: 2.5,
  rowZ: 15,
} as const;

const ROW_TYPES = [ObstacleType.Jump, ObstacleType.LaneChange] as const;
const COLLECTIBLE_SPACING = 2;
/** Bottles over the pit: denser, along the jump arc (extra room before and after the pit). */
const ARC = { spacing: 1.5, overhang: 1.5 } as const;

interface PitVariant {
  readonly code: 'L' | 'C' | 'R' | 'LC' | 'CR';
  readonly label: string;
  readonly fromLane: number;
  readonly toLane: number;
  readonly weight: number;
}

function pitCollectibles(fromLane: number, toLane: number): CollectibleSpawnPoint[] {
  const flat = () => COLLECTIBLE_FLOAT_HEIGHT;
  const center = (PIT_SMALL.zFrom + PIT_SMALL.zTo) / 2;
  const halfArc = (PIT_SMALL.zTo - PIT_SMALL.zFrom) / 2 + ARC.overhang;
  const arc = jumpArc(COLLECTIBLE_FLOAT_HEIGHT, LEVEL_CONFIG.collectibleJumpArcHeight, center, halfArc);
  const points: CollectibleSpawnPoint[] = [];
  // Pit lanes first: the arc line is the segment's signature.
  const lanes = [...Array(WORLD_CONFIG.laneCount).keys()].sort((a, b) => Number(b >= fromLane && b <= toLane) - Number(a >= fromLane && a <= toLane));
  for (const lane of lanes) {
    const inPit = lane >= fromLane && lane <= toLane;
    points.push(
      ...collectibleLine(
        WORLD_CONFIG,
        inPit ? `PIT_ARC_L${lane}` : `LANE_L${lane}`,
        inPit
          ? [
              { lane, from: 1, to: center - halfArc - ARC.spacing, spacing: COLLECTIBLE_SPACING, height: flat },
              { lane, from: center - halfArc, to: center + halfArc, spacing: ARC.spacing, height: arc },
              { lane, from: center + halfArc + COLLECTIBLE_SPACING, to: PIT_SMALL.length - 1, spacing: COLLECTIBLE_SPACING, height: flat },
            ]
          : [{ lane, from: 1, to: PIT_SMALL.length - 1, spacing: COLLECTIBLE_SPACING, height: flat }],
      ),
    );
  }
  return points;
}

function createPitSmall(variant: PitVariant): SegmentDefinition {
  const id = `SEG_PIT_SMALL_${variant.code}_20M`;
  return {
    id,
    kind: SegmentKind.Straight,
    displayName: `Baugrube klein (${variant.label}) 20 m`,
    length: PIT_SMALL.length,
    width: WORLD_CONFIG.trackWidth,
    visualAssetId: id,

    entry: entryConnector(),
    exits: [straightExit(PIT_SMALL.length)],
    lanes: standardLanes(WORLD_CONFIG),

    terrain: {
      surfaces: [],
      gaps: [{ id: 'GAP_PIT', ...laneArea(WORLD_CONFIG, variant.fromLane, variant.toLane, PIT_SMALL.zFrom, PIT_SMALL.zTo), depth: PIT_SMALL.depth }],
      ceilings: [],
    },

    obstacleSpawnPoints: obstacleRow(WORLD_CONFIG, 'R15', PIT_SMALL.rowZ, ROW_TYPES),
    obstacleRowLayouts: { sparse: ['R15'], dense: ['R15'] },

    collectibleSpawnPoints: pitCollectibles(variant.fromLane, variant.toLane),

    ...moduleSides(PIT_SMALL.length),

    generation: {
      weight: variant.weight,
      minDistance: 150,
      minSegmentsAfterJunction: 1,
      family: 'PIT_SMALL',
      tags: ['pit', 'terrain'],
    },
  };
}

export const PIT_SMALL_SEGMENTS: readonly SegmentDefinition[] = [
  createPitSmall({ code: 'L', label: 'links', fromLane: 0, toLane: 0, weight: 0.08 }),
  createPitSmall({ code: 'C', label: 'Mitte', fromLane: 1, toLane: 1, weight: 0.1 }),
  createPitSmall({ code: 'R', label: 'rechts', fromLane: 2, toLane: 2, weight: 0.08 }),
  createPitSmall({ code: 'LC', label: 'links + Mitte', fromLane: 0, toLane: 1, weight: 0.045 }),
  createPitSmall({ code: 'CR', label: 'Mitte + rechts', fromLane: 1, toLane: 2, weight: 0.045 }),
];
